import React from 'react';
import PropTypes from 'prop-types';

import Slider from '../../library/slider/slider';
import Tooltip from '../../library/tooltip/tooltip';
import Counter from './components/counter/counter';
import MilesPerYearInput from './components/miles-per-year-input/miles-per-year-input';
import { calculateLifeExpectancy } from './tire-life-cost-info-utils';

import { useMessage } from '../../shared/hooks/hooks';
import useTireLifeCost from './hooks/use-tile-life-cost';

import { TREADWELL_WORNTIRE_TREAD_LIFE_ESTIMATION_MSG } from '../../shared/constants/messages';
import { TIRE_LIFE_TOOLTIPS } from './constants/tire-life-cost-constants';

import './tire-life-cost-info.scss';

function TireLifeCostInfo(props) {
  const {
    className = '',
    hideLifeAndCostDetails = false,
    max,
    min,
    product,
    showTooltip = false
  } = props;

  const {
    miles,
    setMiles,
    onMilesInputBlur,
    onMilesInputChange,
    milesInputError
  } = useTireLifeCost();

  const { milesPerDollar, mileage } = product?.recommendationData || {};
  const { min: minMileage, median, warranty } = mileage || {};
  const fallbackMessage =
    'Tread life is an estimate based on actual mileage for this tire on your vehicle model, in your area, with proper maintenance.';
  const message = useMessage([TREADWELL_WORNTIRE_TREAD_LIFE_ESTIMATION_MSG]);
  const tooltipMessage =
    message?.[TREADWELL_WORNTIRE_TREAD_LIFE_ESTIMATION_MSG];

  const { tireLifeYears, tireLifeMonths } = calculateLifeExpectancy(
    product,
    15 * 1000,
    miles * 1000
  );

  const formatDisplayValue = val => {
    if (!val) return 0;
    if (val) {
      return Math.round(val / 1000);
    }
  };

  return (
    <div className={`${className} tire-life-cost-info`} styleName="container">
      <div
        className="tire-life-cost-info__miles-section"
        styleName="miles-section"
      >
        <MilesPerYearInput
          defaultValue={miles}
          error={milesInputError}
          label="Input Your Miles Driven Per Year"
          max={max}
          min={min}
          onBlur={onMilesInputBlur}
          onChange={onMilesInputChange}
          value={miles}
        />

        <div styleName="slider">
          <Slider
            markerPrefix="5K"
            markerSuffix="55K"
            max={55}
            min={5}
            onChange={e => setMiles(e)}
            showTooltip
            value={miles}
          />
        </div>
      </div>

      {!hideLifeAndCostDetails && (
        <div
          className="tire-life-cost-info__counter-section"
          styleName="counter-section"
        >
          <div styleName="wrapper">
            <div styleName="column-first--row">
              {typeof tireLifeYears === 'number' && (
                <div styleName="item">
                  <Counter countTo={tireLifeYears} isWholeNum start>
                    {({ value }) => <span styleName="value">{value}</span>}
                  </Counter>
                  <span styleName="label">years</span>
                </div>
              )}

              <div styleName="item">
                {typeof tireLifeMonths === 'number' ? (
                  <Counter countTo={tireLifeMonths} isWholeNum start>
                    {({ value }) => <span styleName="value">{value}</span>}
                  </Counter>
                ) : (
                  <span styleName="na">N/A</span>
                )}
                <span styleName="label">
                  months
                  {showTooltip && (
                    <Tooltip
                      content={TIRE_LIFE_TOOLTIPS.tireLife}
                      styleName="tooltip"
                    />
                  )}
                </span>
              </div>
            </div>
          </div>
          <div styleName="wrapper">
            <div styleName="column">
              {milesPerDollar ? (
                <Counter countTo={milesPerDollar} isWholeNum start>
                  {({ value }) => <span styleName="value">{value}</span>}
                </Counter>
              ) : (
                <span styleName="na">N/A</span>
              )}
              <span styleName="label">
                miles per dollar
                {showTooltip && (
                  <Tooltip
                    content={TIRE_LIFE_TOOLTIPS.milesPerDollar}
                    styleName="tooltip"
                  />
                )}
              </span>
            </div>
          </div>
          <div styleName="wrapper">
            <div styleName="column-first">
              {formatDisplayValue(median) ? (
                <span>
                  <Counter
                    countTo={formatDisplayValue(minMileage)}
                    isWholeNum
                    start
                    suffix="K"
                  >
                    {({ value }) => <span styleName="value">{value}</span>}
                  </Counter>
                  <span styleName="value--no-margin">-</span>
                  <Counter
                    countTo={formatDisplayValue(median)}
                    isWholeNum
                    start
                    suffix="K"
                  >
                    {({ value }) => <span styleName="value">{value}</span>}
                  </Counter>
                </span>
              ) : (
                <span styleName="na">N/A</span>
              )}
              <span styleName="label">
                tread life estimate
                {showTooltip && (
                  <Tooltip
                    content={tooltipMessage || fallbackMessage}
                    styleName="tooltip"
                  />
                )}
              </span>
            </div>
          </div>
          <div styleName="wrapper">
            <div styleName="column">
              {formatDisplayValue(warranty) ? (
                <Counter
                  countTo={formatDisplayValue(warranty)}
                  isWholeNum
                  start
                  suffix="K"
                >
                  {({ value }) => <span styleName="value">{value}</span>}
                </Counter>
              ) : (
                <span styleName="na">N/A</span>
              )}
              <span styleName="label">tire warranty</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

TireLifeCostInfo.propTypes = {
  className: PropTypes.string,
  hideLifeAndCostDetails: PropTypes.bool,
  isTreadwellPage: PropTypes.bool,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  product: PropTypes.object,
  showTooltip: PropTypes.bool
};

export default TireLifeCostInfo;
